import * as React from "react";

import AppModals from "@editor/components/AppModals";
import { DebugModal } from "@editor/components/editor/debug/DebugPanel";
import Flows from "@editor/components/flows/Flows";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useReploHotkeys } from "@editor/hooks/useHotkeys";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { useEditorDispatch, useEditorSelector } from "@editor/store";
import { trpc } from "@editor/utils/trpc";
import {
  selectInternalDebugModeOn,
  setDebugPanelVisibility,
} from "@reducers/core-reducer";

import { ToastManager } from "@replo/design-system/components/alert/Toast";
import { skipToken } from "@tanstack/react-query";
import { Outlet } from "react-router-dom";

import { Loader } from "../common/Loader";
import DashboardSidebar from "./DashboardSidebar";

const DashboardLayout: React.FC<React.PropsWithChildren> = ({ children }) => {
  const dispatch = useEditorDispatch();
  const isDebugPanelVisible = useEditorSelector(selectInternalDebugModeOn);
  const toggleDebugMode = React.useCallback(() => {
    dispatch(setDebugPanelVisibility(!isDebugPanelVisible));
  }, [dispatch, isDebugPanelVisible]);
  useReploHotkeys({ debug: toggleDebugMode });
  const { user } = useCurrentUser();
  const projectId = useCurrentProjectId();

  const { isFetching: isProjectInitialized, data: isInitialized } =
    trpc.nextJSProject.checkAndInitializeProjectFiles.useQuery(
      projectId && isFeatureEnabled("reaplo") ? { projectId } : skipToken,
    );

  const params = new URLSearchParams({ utm_source: "dashboard" });

  if (user) {
    params.append("email", user.email);
    if (user.firstName) {
      params.append("name", user.firstName);
    }
  }

  // Show loading state when we're inside shop context (projectId) until we have the defaultFileId data
  const shouldShowLoading =
    Boolean(projectId) &&
    isFeatureEnabled("reaplo") &&
    (isProjectInitialized || !isInitialized);

  return (
    <div className="flex h-screen flex-col">
      <ToastManager />
      <AppModals />
      {user && <Flows entityType="user" />}
      {isDebugPanelVisible && <DebugModal />}
      <div className="bg-sheet grid w-screen grow grid-cols-[1fr] no-scrollbar gap-x-4 p-3 overflow-y-scroll md:grid-cols-[fit-content(224px)_1fr]">
        <DashboardSidebar />
        <div className="shadow-selectable border-0.5 border-border no-scrollbar rounded-[10px] bg-white p-6 pb-0 overflow-y-scroll flex flex-col items-center h-full">
          {!shouldShowLoading ? (
            <div className="w-full max-w-[1440px] overflow-y-scroll no-scrollbar flex flex-col items-center h-full">
              <Outlet />
              {children}
            </div>
          ) : (
            <Loader />
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
